import { FC, useCallback, useMemo } from 'react';
import { Button, Space } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowDown, TbArrowUp, TbChevronDown, TbChevronUp } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { usePlatformHotkeys as useHotkeys } from '@/hooks/usePlatformHotkeys';
import { optionKey } from '@/utilities';

import { SmallTooltip } from '../../tooltips';

interface PreviousNextResourceButtonGroupProps {
    currentIndex?: number;
    enableHotkeys?: boolean;
    getResourceId?: (resource: any) => string | number | undefined;
    getResourcePath?: (resource: any) => string | undefined;
    onNextResource?: (resource: any, index: number) => void;
    onPreviousResource?: (resource: any, index: number) => void;
    resource: any;
    resources: any;
    singularResourceLabel: string;
    pluralResourceLabel: string;
    showCount?: boolean;
}

const PreviousNextResourceButtonGroup: FC<PreviousNextResourceButtonGroupProps> = ({
    currentIndex,
    enableHotkeys = true,
    getResourceId,
    getResourcePath,
    onNextResource,
    onPreviousResource,
    resource,
    resources,
    singularResourceLabel,
    pluralResourceLabel,
    showCount,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    const navigate = useNavigate();
    const resourceList = useMemo(() => (Array.isArray(resources) ? resources : []), [resources]);
    const resolveResourceId = useCallback(
        (item: any) => {
            if (getResourceId) {return getResourceId(item);}

            return item?.id;
        },
        [getResourceId],
    );
    const resolvedCurrentIndex = useMemo(() => {
        if (typeof currentIndex === 'number') {return currentIndex;}

        const currentResourceId = resolveResourceId(resource);

        if (currentResourceId === undefined) {return -1;}

        return resourceList.findIndex((item: any) => resolveResourceId(item) === currentResourceId);
    }, [currentIndex, resolveResourceId, resource, resourceList]);
    const canNavigate = resourceList.length > 1 && resolvedCurrentIndex >= 0;
    const previousIndex = canNavigate ? (resolvedCurrentIndex - 1 + resourceList.length) % resourceList.length : -1;
    const nextIndex = canNavigate ? (resolvedCurrentIndex + 1) % resourceList.length : -1;
    const previousResource = previousIndex >= 0 ? resourceList[previousIndex] : undefined;
    const nextResource = nextIndex >= 0 ? resourceList[nextIndex] : undefined;

    const routeForResource = useCallback(
        (item: any) => {
            if (getResourcePath) {return getResourcePath(item);}

            const id = resolveResourceId(item);

            return id === undefined ? undefined : `/${pluralResourceLabel}/${id}`;
        },
        [getResourcePath, pluralResourceLabel, resolveResourceId],
    );

    const handlePreviousResource = useCallback(() => {
        if (!canNavigate || !previousResource) {return;}

        if (onPreviousResource) {
            onPreviousResource(previousResource, previousIndex);
            return;
        }

        const route = routeForResource(previousResource);

        if (route) {navigate(route);}
    }, [canNavigate, navigate, onPreviousResource, previousIndex, previousResource, routeForResource]);

    const handleNextResource = useCallback(() => {
        if (!canNavigate || !nextResource) {return;}

        if (onNextResource) {
            onNextResource(nextResource, nextIndex);
            return;
        }

        const route = routeForResource(nextResource);

        if (route) {navigate(route);}
    }, [canNavigate, navigate, nextIndex, nextResource, onNextResource, routeForResource]);

    useHotkeys('alt+up', (event) => {
        if (!enableHotkeys) {return;}

        event.preventDefault();
        handlePreviousResource();
    });

    useHotkeys('alt+down', (event) => {
        if (!enableHotkeys) {return;}

        event.preventDefault();
        handleNextResource();
    });

    return (
        <>
            {showCount && (
                <small
                    style={{
                        verticalAlign: '0.1rem',
                        marginRight: '0.5rem',
                        color: token.colorTextTertiary,
                    }}
                >
                    {resolvedCurrentIndex >= 0 ? resolvedCurrentIndex + 1 : 0} / {resourceList.length}
                </small>
            )}
            <Space.Compact>
                <SmallTooltip
                    title={
                        <>
                            Go to Previous {singularResourceLabel && capitalCase(singularResourceLabel)}{' '}
                            <span style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                {optionKey} <TbArrowUp style={{ verticalAlign: '-1px' }} />
                            </span>
                        </>
                    }
                    placement='topRight'
                    padding='0.5rem 0'
                >
                    <Button disabled={!canNavigate} onClick={handlePreviousResource} size='small'>
                        <TbChevronUp style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />
                    </Button>
                </SmallTooltip>
                <SmallTooltip
                    title={
                        <>
                            Go to Next {singularResourceLabel && capitalCase(singularResourceLabel)}{' '}
                            <span style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                {optionKey} <TbArrowDown style={{ verticalAlign: '-1px' }} />
                            </span>
                        </>
                    }
                    placement='topRight'
                    padding='0.5rem 0'
                >
                    <Button
                        disabled={!canNavigate}
                        onClick={handleNextResource}
                        size='small'
                        style={{
                            borderTopRightRadius: '0.25rem',
                            borderBottomRightRadius: '0.25rem',
                        }}
                    >
                        <TbChevronDown style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />
                    </Button>
                </SmallTooltip>
            </Space.Compact>
        </>
    );
};

export default PreviousNextResourceButtonGroup;
